import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Card, Row, Input, Col, DatePicker, Form, message, Select, Modal } from 'antd';
import { connect } from 'react-redux';
import { State, States } from 'sigt';
import { useWindowDimensions } from '../../utils/hooks';
import { ColumnsType } from 'antd/lib/table';
import moment, { Moment } from 'moment';
import {
  AuditMovementDescription,
  AuditMovementFilters,
  AuditMovementRecord,
  getAuditMovementDescriptions,
  getAuditMovements,
} from '../../services/auditMovements';

interface AuditMovementFormValues {
  email?: string;
  date?: Moment;
  endpoint?: string;
  ip?: string;
}

const TableAuditMovements: React.FC<PropsTable> = ({ thm, auth }) => {
  const [loading, setLoading] = useState(false);
  const { width } = useWindowDimensions();
  const [data, setData] = useState<AuditMovementRecord[]>([]);
  const [pageTotal, setPageTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [filters, setFilters] = useState<AuditMovementFilters>({});
  const [descriptions, setDescriptions] = useState<AuditMovementDescription[]>([]);
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState<AuditMovementRecord | null>(null);

  const fetchAuditMovements = useCallback(
    async (page = 1, currentFilters: AuditMovementFilters = filters) => {
      if (!auth.token) return;

      try {
        setLoading(true);
        const response = await getAuditMovements(page, auth.token, currentFilters);
        setData(response.logs || []);
        setPageTotal(response.logsTotal || 0);
        setCurrentPage(page);
      } catch (error) {
        setData([]);
        setPageTotal(0);
        message.error(error?.response?.data?.message || error?.message || 'No se pudo cargar la auditoria de movimientos.');
      } finally {
        setLoading(false);
      }
    },
    [auth.token, filters]
  );

  useEffect(() => {
    const fetchDescriptions = async () => {
      if (!auth.token) return;
      try {
        const res = await getAuditMovementDescriptions(auth.token);
        console.log('Descripciones obtenidas:', res);
        setDescriptions(res);
      } catch (e) {
        setDescriptions([]);
      }
    };
    fetchDescriptions();
  }, [auth.token]);

  useEffect(() => {
    fetchAuditMovements(1, filters);
  }, [fetchAuditMovements, filters]);

  const handleShowModal = (record: AuditMovementRecord) => {
    setModalData(record);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setModalData(null);
  };

  const columns: ColumnsType<AuditMovementRecord> = [
    {
      title: 'Id Log',
      dataIndex: 'id_log',
      key: 'id_log',
      align: 'left',
      render: (text) => <>{text ?? 'Sin información'}</>,
    },
    {
      title: 'Dirección IP',
      dataIndex: 'ip',
      key: 'ip',
      align: 'left',
      render: (text) => <>{text ?? 'Sin información'}</>,
    },
    {
      title: 'Nombre del usuario',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      align: 'left',
      render: (text) => <>{text ?? 'Sin información'}</>,
    },
    {
      title: 'Correo del usuario',
      dataIndex: 'nombre_de_usuario',
      key: 'nombre_de_usuario',
      align: 'left',
      render: (text) => <>{text ?? 'Sin información'}</>,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      align: 'left',
      render: (text) => <>{text ? moment.utc(text).format('DD/MM/YYYY HH:mm') : 'Sin información'}</>,
    },
    {
      title: 'Descripcion',
      dataIndex: 'descripcion',
      key: 'descripcion',
      align: 'left',
      render: (text) => <>{text ?? 'Sin información'}</>,
    },
    {
      title: 'Detalles',
      key: 'detalles',
      align: 'center',
      render: (_, record) => (
        <Button shape='round' size='middle' onClick={() => handleShowModal(record)}>
          Ver detalles
        </Button>
      ),
    },
  ];

  const onFinish = ({ email, date, endpoint, ip }: AuditMovementFormValues) => {
    const nextFilters: AuditMovementFilters = {};
    if (email) nextFilters.email = email.trim();
    if (date) nextFilters.day = date.toDate();
    if (endpoint) nextFilters.endpoint = endpoint;
    if (ip) nextFilters.ip = ip.trim();
    setFilters(nextFilters);
  };

  const handleClearFilters = () => {
    form.resetFields();
    setFilters({});
  };

  return (
    <>
      <Card
        title='Auditoria de movimientos'
        headStyle={{ backgroundColor: thm.primaryColor, padding: width < 992 ? '0 10px' : '0 20px', color: '#fff' }}
        bodyStyle={{ overflowY: 'auto' }}
      >
        <Form form={form} layout='vertical' onFinish={onFinish}>
          <Row gutter={16} justify='start' align='bottom'>
            <Col xs={24} md={8}>
              <Form.Item label='Correo del usuario' name='email'>
                <Input placeholder='correo@dominio.com' disabled={loading} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label='Dirección IP' name='ip'>
                <Input placeholder='0.0.0.0' disabled={loading} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label='Fecha' name='date'>
                <DatePicker style={{ width: '100%' }} format='DD/MM/YYYY' disabled={loading} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label='Descripción' name='endpoint'>
                <Select
                  showSearch
                  allowClear
                  placeholder='Selecciona descripción'
                  optionFilterProp='children'
                  disabled={loading}
                  filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                >
                  {Array.isArray(descriptions) &&
                    descriptions.map((desc) => (
                      <Select.Option key={desc.id_descripcion} value={desc.id_descripcion}>
                        {desc?.descripcion || 'Por definir'}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Row gutter={8} style={{ paddingBlock: 24 }}>
                <Col span={12}>
                  <Button block type='primary' htmlType='submit' loading={loading}>
                    Buscar
                  </Button>
                </Col>
                <Col span={12}>
                  <Button block onClick={handleClearFilters} disabled={loading}>
                    Limpiar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>

        <Row gutter={16} justify='start' align='middle'>
          <Col span={24} style={{ marginBlock: 20 }}>
            <Table
              className='tableScroll'
              rowKey='id_log'
              scroll={{ x: 'max-content' }}
              style={{ height: '100%' }}
              size='large'
              loading={loading}
              columns={columns}
              dataSource={data}
              pagination={{
                current: currentPage,
                total: pageTotal,
                onChange: (page) => fetchAuditMovements(page),
                pageSize,
                size: 'small',
                showSizeChanger: false,
              }}
            />
          </Col>
        </Row>
      </Card>
      <Modal
        visible={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
        title={`Detalles del log${modalData?.id_log ? ` #${modalData.id_log}` : ''}`}
      >
        <div>
          <b>Detalles del log</b>
          <div
            style={{
              marginBottom: 20,
              background: '#f6f6f6',
              padding: 12,
              borderRadius: 4,
              justifyContent: 'space-between',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <span>
              <b>Id log:</b> {modalData?.id_log || 'Sin información'}
            </span>
            <span>
              <b>Dirección IP:</b> {modalData?.ip || 'Sin información'}
            </span>
            <span>
              <b>Descripción:</b> {modalData?.descripcion || 'Sin información'}
            </span>
            <span>
              <b>Fecha:</b> {modalData?.fecha ? moment.utc(modalData.fecha).format('DD/MM/YYYY HH:mm') : 'Sin información'}
            </span>
          </div>
        </div>

        {/* Sección de datos del usuario */}
        <div style={{ marginBottom: 16 }}>
          <b>Datos del usuario</b>
          <div
            style={{
              marginBottom: 20,
              background: '#f6f6f6',
              padding: 12,
              borderRadius: 4,
              justifyContent: 'space-between',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <span>
              <b>Nombre completo:</b> {modalData?.nombre_completo || 'Sin información'}
            </span>
            <span>
              <b>Documento:</b>{' '}
              {modalData?.nacionalidad && modalData?.cedula ? `${modalData.nacionalidad}${modalData.cedula}` : 'Sin información'}
            </span>
            <span>
              <b>Correo:</b> {modalData?.nombre_de_usuario || 'Sin información'}
            </span>
            <span>
              <b>Teléfono:</b> {modalData?.telefono || 'Sin información'}
            </span>
            <span>
              <b>Dirección:</b> {modalData?.direccion || 'Sin información'}
            </span>
            <span>
              <b>Cargo:</b> {modalData?.cargo || 'Sin información'}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <b>Params</b>
          <pre style={{ background: '#f6f6f6', padding: 12, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
            {modalData?.params
              ? typeof modalData.params === 'string'
                ? modalData.params.trim() === '{}'
                  ? 'Sin información'
                  : modalData.params
                : JSON.stringify(modalData.params, null, 2)
              : 'Sin información'}
          </pre>
        </div>
        <div>
          <b>Body</b>
          <pre style={{ background: '#f6f6f6', padding: 12, borderRadius: 4, maxHeight: 300, overflow: 'auto' }}>
            {modalData?.body
              ? typeof modalData.body === 'string'
                ? modalData.body
                : JSON.stringify(modalData.body, null, 2)
              : 'Sin información'}
          </pre>
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = ({ auth, thm }: State) => ({ auth, thm });

export default connect(mapStateToProps, {})(TableAuditMovements);

interface PropsTable {
  auth: States.Auth;
  thm: States.ThemeColors;
}
